import { useMemo, useCallback } from "react";
import { useAppSelector } from "../../app";

export const useGroupBlockCategorizations = (groupBlockId?: string) => {
  const configMap = useAppSelector((state) => state.configMapReducer.configMap);
  const categorizationAnnotations = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState.categorizationAnnotations,
  );

  const groupBlock = useMemo(() => {
    return configMap?.groupBlocks?.find((gb) => gb.id === groupBlockId);
  }, [configMap, groupBlockId]);

  const categorizationLinks = useMemo(() => {
    if (!groupBlock) {
      return [];
    }

    return (
      groupBlock.categorizationLinks
        ?.filter((link) => link.groupBlockId)
        ?.map((link) => ({
          id: link.id,
          categorization: link.categorization,
          required: link.required,
        })) ?? []
    );
  }, [groupBlock]);

  const sortedCategorizationLinks = useMemo(() => {
    return categorizationLinks.sort(
      (a, b) =>
        (a.categorization.index ?? Number.MAX_VALUE) -
        (b.categorization.index ?? Number.MAX_VALUE),
    );
  }, [categorizationLinks]);

  const categorizations = useMemo(() => {
    return categorizationLinks.map((link) => link.categorization);
  }, [categorizationLinks]);

  const getAnnotationValue = useCallback(
    (categorizationId: string, index: number = 1): string | undefined => {
      return categorizationAnnotations?.find(
        (ca) =>
          ca.categorizationId === categorizationId &&
          ca.index === index &&
          ca.groupBlockId === groupBlockId,
      )?.value;
    },
    [categorizationAnnotations, groupBlockId],
  );

  const hasRequiredCategorization = useCallback(
    (categorizationId: string): boolean => {
      return categorizationLinks.some(
        (link) => link.categorization.id === categorizationId && link.required,
      );
    },
    [categorizationLinks],
  );

  return {
    categorizationLinks: sortedCategorizationLinks,
    categorizations,
    getAnnotationValue,
    hasRequiredCategorization,
  };
};
